"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const ksuid_1 = __importDefault(require("ksuid"));
const utils = __importStar(require("./utils"));
const websocket_1 = require("./websocket");
class OADAClient {
    constructor(config) {
        this._token = "";
        this._domain = "";
        this._concurrency = 1;
        this._domain = config.domain.replace(/^https:\/\//, "");
        this._token = config.token || this._token;
        this._concurrency = config.concurrency || this._concurrency;
        this._ws = new websocket_1.WebSocketClient(this._domain, this._concurrency);
    }
    clone(token) {
        const c = new OADAClient({
            domain: this._domain,
            token: token,
            concurrency: this._concurrency,
            _ws: this._ws,
        });
        return c;
    }
    getToken() {
        return this._token;
    }
    getDomain() {
        return this._domain;
    }
    disconnect() {
        return this._ws.disconnect();
    }
    async get(request) {
        const topLevelResponse = await this._ws.request({
            method: "get",
            headers: {
                authorization: `Bearer ${this._token}`,
            },
            path: request.path,
        }, undefined, request.timeout);
        if (request.tree) {
            const arrayPath = utils.toArrayPath(request.path);
            const subTree = utils.getObjectAtPath(request.tree, arrayPath);
            topLevelResponse.data = await this._recursiveGet(request.path, subTree, topLevelResponse.data || {});
        }
        if (request.watchCallback) {
            const rev = topLevelResponse.headers
                ? topLevelResponse.headers["x-oada-rev"]
                : undefined;
            await this.watch({
                path: request.path,
                rev,
                watchCallback: request.watchCallback,
            });
        }
        return topLevelResponse;
    }
    async watch(request) {
        let headers = {};
        if (request.rev) {
            headers["x-oada-rev"] = request.rev;
        }
        const r = await this._ws.request({
            method: "watch",
            headers: Object.assign({ authorization: `Bearer ${this._token}` }, headers),
            path: request.path,
        }, (resp) => {
            for (const change of resp.change) {
                request.watchCallback(change);
            }
        }, request.timeout);
        if (r.status !== 200) {
            throw new Error("Watch request failed!");
        }
        return Array.isArray(r.requestId) ? r.requestId[0] : r.requestId;
    }
    async unwatch(requestId) {
        return await this._ws.request({
            path: "",
            headers: {
                authorization: "",
            },
            method: "unwatch",
            requestId: requestId,
        });
    }
    async _recursiveGet(path, subTree, data) {
        if (!subTree || !data) {
            throw new Error("Path mismatch.");
        }
        if (subTree["_type"]) {
            data = (await this.get({ path })).data || {};
        }
        let children;
        if (subTree["*"]) {
            children = Object.keys(data).reduce((acc, key) => {
                if (data && typeof data[key] == "object") {
                    acc.push({ treeKey: "*", dataKey: key });
                }
                return acc;
            }, []);
        }
        else {
            children = Object.keys(subTree || {}).reduce((acc, key) => {
                if (data && typeof data[key] == "object") {
                    acc.push({ treeKey: key, dataKey: key });
                }
                return acc;
            }, []);
        }
        let promises = children.map(async (item) => {
            const childPath = path + "/" + item.dataKey;
            if (!data) {
                return;
            }
            const res = await this._recursiveGet(childPath, subTree[item.treeKey], data[item.dataKey]);
            data[item.dataKey] = res;
            return;
        });
        return await Promise.all(promises).then(() => {
            return data;
        });
    }
    async put(request) {
        const pathArray = utils.toArrayPath(request.path);
        if (request.tree) {
            let retryCount = 0;
            let linkObj = null;
            let newResourcePathArray = [];
            for (let i = pathArray.length - 1; i >= 0; i--) {
                const partialPathArray = pathArray.slice(0, i + 1);
                const treeObj = utils.getObjectAtPath(request.tree, partialPathArray);
                if ("_type" in treeObj) {
                    const contentType = treeObj["_type"];
                    const partialPath = utils.toStringPath(partialPathArray);
                    const resourceCheckResult = await this._resourceExists(partialPath);
                    if (resourceCheckResult.exist) {
                        if (linkObj && newResourcePathArray.length > 0) {
                            const linkPutResponse = await this.put({
                                path: utils.toStringPath(newResourcePathArray),
                                contentType,
                                data: linkObj,
                                revIfMatch: resourceCheckResult.rev,
                            }).catch((msg) => {
                                if (msg.status == 412) {
                                    return msg;
                                }
                                else {
                                    throw new Error(`Error: ${msg.statusText}`);
                                }
                            });
                            if (linkPutResponse.status == 412) {
                                if (retryCount++ < 5) {
                                    await utils.delay(1000 * (retryCount * retryCount + Math.random()));
                                    i = pathArray.length - 1;
                                    continue;
                                }
                                else {
                                    throw Error("If-match failed.");
                                }
                            }
                        }
                        break;
                    }
                    else {
                        const relativePathArray = newResourcePathArray.slice(i + 1);
                        const newResource = linkObj
                            ? utils.createNestedObject(linkObj, relativePathArray)
                            : {};
                        const resourceId = await this._createResource(contentType, newResource);
                        linkObj =
                            "_rev" in treeObj
                                ? { _id: resourceId, _rev: 0 }
                                : { _id: resourceId };
                        newResourcePathArray = partialPathArray.slice();
                    }
                }
            }
        }
        let contentType = request.contentType ||
            (request.data && request.data["_type"]) ||
            (request.tree
                ? utils.getObjectAtPath(request.tree, pathArray)["_type"]
                : "application/json");
        return this._ws.request({
            method: "put",
            headers: Object.assign({ authorization: `Bearer ${this._token}`, "content-type": contentType }, (request.revIfMatch && {
                "if-match": request.revIfMatch.toString(),
            })),
            path: request.path,
            data: request.data,
        }, undefined, request.timeout);
    }
    async post(request) {
        const pathArray = utils.toArrayPath(request.path);
        const data = request.data;
        if (request.tree) {
            request.data = {};
            await this.put(request);
        }
        let contentType = request.contentType ||
            (request.data && request.data["_type"]) ||
            (request.tree
                ? utils.getObjectAtPath(request.tree, pathArray)["_type"]
                : "application/json");
        return this._ws.request({
            method: "post",
            headers: {
                authorization: `Bearer ${this._token}`,
                "content-type": contentType,
            },
            path: request.path,
            data,
        }, undefined, request.timeout);
    }
    async head(request) {
        return this._ws.request({
            method: "head",
            headers: {
                authorization: `Bearer ${this._token}`,
            },
            path: request.path,
        }, undefined, request.timeout);
    }
    async delete(request) {
        return this._ws.request({
            method: "delete",
            headers: {
                authorization: `Bearer ${this._token}`,
            },
            path: request.path,
        }, undefined, request.timeout);
    }
    async _createResource(contentType, data) {
        const resourceId = "resources/" + ksuid_1.default.randomSync().string;
        await this.put({
            path: "/" + resourceId,
            data,
            contentType,
        });
        return resourceId;
    }
    async _resourceExists(path) {
        if (path === "/resources") {
            return { exist: true };
        }
        const headResponse = await this.head({
            path,
        }).catch((msg) => {
            if (msg.status == 404) {
                return msg;
            }
            else if (msg.status == 403 && path.match(/^\/resources/)) {
                return { status: 404 };
            }
            else {
                throw new Error(`Error: ${msg.statusText}`);
            }
        });
        if (headResponse.status == 200) {
            return { exist: true, rev: headResponse.headers["x-oada-rev"] };
        }
        else if (headResponse.status == 404) {
            return { exist: false };
        }
        else {
            throw Error("Status code is neither 200 nor 404.");
        }
    }
}
exports.OADAClient = OADAClient;
//# sourceMappingURL=client.js.map